import {jsonArrayMember, jsonMember, jsonObject} from "typedjson";
import {DxGridColumn} from "./column/dx-grid-column";
import {DxGridToolbarOptions} from "./toolbar/dx-grid-toolbar-options";
import {DxGridPaginationOptions} from "./dx-grid-pagination-options";
import {DxInfiniteScrollOptions} from "./dx-infinite-scroll-options";
import {DxGridFilter} from "../filter/dx-grid-filter";
import {PitRequestHiddenCriteria} from "../../../pit-shared/domain/requests/pit-request-hidden-criteria";


/** Опций грида. */
@jsonObject
export class DxGridOptions {
  /** Идентификатор объекта опции (заточка под хранение опций в БД). */
  @jsonMember({name: "id", constructor: Number, emitDefaultValue: false, isRequired: false})
  private _id: number;

  /** Уникальный строковвый идент грида */
  @jsonMember({name: "ident", constructor: String, emitDefaultValue: false, isRequired: false})
  private _ident: string;

  /** Название грида. */
  @jsonMember({name: "title", constructor: String, emitDefaultValue: false, isRequired: false})
  private _title: string;

  /**
   * Автонастройка высоты грида под содержимое.
   * Если не установлено, то грид должен быть встроен в контейнер с фиксированной высотой, иначе он не отобразится.
   */
  @jsonMember({name: "autoHeight", constructor: Boolean, emitDefaultValue: false, isRequired: false})
  private _autoHeight: boolean = true;

  /** Переключение грида на серверную модель данных. */
  @jsonMember({name: "useServerSideDataModel", constructor: Boolean, emitDefaultValue: false, isRequired: false})
  private _useServerSideDataModel: boolean = false;

  /**
   * Уникальный строковый идент источника данных для данного грида.
   * По этому иденту через механизм DI будет получен инстанс источника данных.
   */
  @jsonMember({name: "dataSourceIdent", constructor: String, emitDefaultValue: false, isRequired: false})
  private _dataSourceIdent: string;

  /** Настройки бесконечного сролирования. */
  @jsonMember({name: "infiniteScrollOptions", constructor: DxInfiniteScrollOptions, emitDefaultValue: false, isRequired: false})
  private _infiniteScrollOptions: DxInfiniteScrollOptions;


  /**
   * Включение режима пагинации.
   * Если пагинация не используется и активирована серверная модель данных грида,
   * то для пролистования будет применена модель бесконечного скрола. По-сути, таже пагинация,
   * только транспонированная.
   */
  @jsonMember({name: "allowPagination", constructor: Boolean, emitDefaultValue: false, isRequired: false})
  private _allowPagination: boolean = false;

  /** Настройки пагинации. */
  @jsonMember({name: "paginationOptions", constructor: DxGridPaginationOptions, emitDefaultValue: false, isRequired: false})
  private _paginationOptions: DxGridPaginationOptions;

  @jsonMember({constructor: Number, emitDefaultValue: false, isRequired: false})
  public headerHeight: number;

  /**
   * Переключение режимов редактирования данных грида:
   * 1. Редактирование строки напрямую через виджеты ячеек.
   * 2. Редактирование строки через отдельно предоставляемую модальную форму.
   * Эту настроку имеет смысл выставлять дря грида в целом, а не отдельных колонок.
   */
  @jsonMember({name: "useInlineEdit", constructor: Boolean, emitDefaultValue: false, isRequired: false})
  private _useInlineEdit: boolean = false;

  /**
   * Уникальный строковый идент фабрик строк для данного грида.
   * По этому иденту через механизм DI будет получен инстанс фабрики.
   */
  @jsonMember({name: "rowFactoryIdent", constructor: String, emitDefaultValue: false, isRequired: false})
  private _rowFactoryIdent: string;

  /**
   * Уникальный строковый идент провайдера модальных окон для данного грида.
   * По этому иденту через механизм DI будет получен инстанс провайдера модальных окон.
   */
  @jsonMember({name: "modalsProviderIdent", constructor: String, emitDefaultValue: false, isRequired: false})
  private _modalsProviderIdent: string;


  /** Используется \ не используется панель инструментов для данного грида. */
  @jsonMember({name: "showToolbar", constructor: Boolean, emitDefaultValue: false, isRequired: false})
  private _showToolbar: boolean = false;

  /** Опции панели инструментов грида. */
  @jsonMember({name: "toolbar", constructor: DxGridToolbarOptions, emitDefaultValue: false, isRequired: false})
  private _toolbar: DxGridToolbarOptions;

  /**
   * Признак использования внешнего хэндлера для действий на тулбаре для данного грида.
   * Это позволяет использовать логику отрисовки и измененния доступности кнопок на панели инструментов,
   * но саму обработку действий вести вне грида. Работа с гридом идет через API. Это нужно при сильно кастомной
   * обработке действий.
   */
  @jsonMember({name: "externalToolbarHandler", constructor: Boolean, emitDefaultValue: false, isRequired: false})
  private _externalToolbarHandler: boolean = false;

  /**
   * Уникальный строковый идент резолвера действий на панели инструментов данного грида.
   * По этому иденту через механизм DI будет получен инстанс резолвера.
   */
  @jsonMember({name: "actionResolverIdent", constructor: String, emitDefaultValue: false, isRequired: false})
  private _actionResolverIdent: string;


  /** Опции колонок грида. */
  @jsonArrayMember(DxGridColumn, {name: "columns", emitDefaultValue: false, isRequired: false})
  private _columns: DxGridColumn[];

  /** Включение множественной отмеки строк. По умолчанию единичная. */
  @jsonMember({name: "allowMultiSelect", constructor: Boolean, emitDefaultValue: false, isRequired: false})
  private _allowMultiSelect: boolean = false;

  /** Отображение в шапке таблицы строки с фильтрами floatingFilter  */
  @jsonMember({name: "showFiltersBar", constructor: Boolean, emitDefaultValue: false, isRequired: false})
  private _showFiltersBar: boolean = false;

  /**
   * Для постоянных/скрытых критериев рекомендуется поле {@link hiddenCriteria} к использованию.
   * Но если нет возможности уложить query запроса в эту структуру по какой-то причине, либо надо
   * внести данные поля в тело {@link PitRequestBody}, то укладываем сюда
   */
  criterias: Map<string, any> = new Map();

  /**
   * Поле для передачи дополнительных критериев фильтрации и сортировки, которые сливаются
   * с пользовательские критерииями (сначала эти, и поверх пользовательские из {@link DxGridDatasource})
   * См. методы {@link PitActivitiesService.getSortParams}, {@link PitActivitiesService.getFilterParams},
   * которые позже могут стать общими
   */
  @jsonMember({constructor: PitRequestHiddenCriteria, emitDefaultValue: false, isRequired: false})
  hiddenCriteria: PitRequestHiddenCriteria = new PitRequestHiddenCriteria();

  /**
   * node: The rowNode in question.
   * data: The data for the row.
   * api: The grid api.
   * context: The grid context.
   */
  getRowHeight: (params: {node, data, api, context}) => number;

  get hasDictColumns(): boolean {
    if(this._columns && this._columns.length > 0) {
      const dictColumns = this._columns.filter(column => column.dataType.isDictionary);
      return dictColumns && dictColumns.length > 0;
    }
    return false;
  }


  get id(): number {
    return this._id;
  }

  set id(value: number) {
    this._id = value;
  }

  get ident(): string {
    return this._ident;
  }

  set ident(value: string) {
    this._ident = value;
  }

  get title(): string {
    return this._title;
  }

  set title(value: string) {
    this._title = value;
  }

  get useServerSideDataModel(): boolean {
    return this._useServerSideDataModel;
  }

  set useServerSideDataModel(value: boolean) {
    this._useServerSideDataModel = value;
  }

  get dataSourceIdent(): string {
    return this._dataSourceIdent;
  }

  set dataSourceIdent(value: string) {
    this._dataSourceIdent = value;
  }

  get infiniteScrollOptions(): DxInfiniteScrollOptions {
    return this._infiniteScrollOptions;
  }

  set infiniteScrollOptions(value: DxInfiniteScrollOptions) {
    this._infiniteScrollOptions = value;
  }

  get allowPagination(): boolean {
    return this._allowPagination;
  }

  set allowPagination(value: boolean) {
    this._allowPagination = value;
  }

  get paginationOptions(): DxGridPaginationOptions {
    return this._paginationOptions;
  }

  set paginationOptions(value: DxGridPaginationOptions) {
    this._paginationOptions = value;
  }

  get useInlineEdit(): boolean {
    return this._useInlineEdit;
  }

  set useInlineEdit(value: boolean) {
    this._useInlineEdit = value;
  }

  get rowFactoryIdent(): string {
    return this._rowFactoryIdent;
  }

  set rowFactoryIdent(value: string) {
    this._rowFactoryIdent = value;
  }

  get modalsProviderIdent(): string {
    return this._modalsProviderIdent;
  }

  set modalsProviderIdent(value: string) {
    this._modalsProviderIdent = value;
  }

  get showToolbar(): boolean {
    return this._showToolbar;
  }

  set showToolbar(value: boolean) {
    this._showToolbar = value;
  }

  get toolbar(): DxGridToolbarOptions {
    return this._toolbar;
  }

  set toolbar(value: DxGridToolbarOptions) {
    this._toolbar = value;
  }

  get externalToolbarHandler(): boolean {
    return this._externalToolbarHandler;
  }

  set externalToolbarHandler(value: boolean) {
    this._externalToolbarHandler = value;
  }

  get actionResolverIdent(): string {
    return this._actionResolverIdent;
  }

  set actionResolverIdent(value: string) {
    this._actionResolverIdent = value;
  }

  get columns(): DxGridColumn[] {
    return this._columns;
  }

  set columns(value: DxGridColumn[]) {
    this._columns = value;
  }

  get allowMultiSelect(): boolean {
    return this._allowMultiSelect;
  }

  set allowMultiSelect(value: boolean) {
    this._allowMultiSelect = value;
  }

  get showFiltersBar(): boolean {
    return this._showFiltersBar;
  }

  set showFiltersBar(value: boolean) {
    this._showFiltersBar = value;
  }

  get autoHeight(): boolean {
    return this._autoHeight;
  }

  set autoHeight(value: boolean) {
    this._autoHeight = value;
  }
}
